<template>
  <div class="shipment-timeline-container">
    <ShipmentTimeline
      id="shipment-timeline"
      :disabled="!editable || isSaving"
      :shipment-timeline-items="
        isShowingTimeline ? shipmentTimelineItems : milestones
      "
      :tender-requests="tenderRequests"
      :service-incident-types="serviceIncidentTypes.data"
      :require-both-dates="requireBothArrivalAndDepartureOnStops"
      :show-entries-without-locations="showEntriesWithoutLocations"
      @delete-shipment-event="onDeleteShipmentEvent"
      @edit-shipment-event="onEditShipmentEvent"
      @delete-shipment-service-incident="onDeleteShipmentServiceIncident"
      @edit-shipment-service-incident="onEditShipmentServiceIncident"
    />

    <ShipmentEventModal
      v-model:visible="eventModal.visible"
      :shipment-event="eventModal.event"
      :event-type-options="filteredShipmentEventTypeOptions"
      :severity-options="shipmentEventSeverityOptions"
      :shipment-stops="shipmentStops"
      :shipment="shipment"
      :show-source="eventModal.showSource"
      :client-requires-both-dates="requireBothArrivalAndDepartureOnStops"
      @save="onSaveShipmentEvent"
    />

    <ShipmentServiceIncidentModal
      v-model:visible="incidentModal.visible"
      :service-incident="incidentModal.incident"
      :types="serviceIncidentTypes.data"
      :severity-options="shipmentEventSeverityOptions"
      :shipment="shipment"
      :shipment-stops="shipmentStops"
      @save="onSaveServiceIncident"
    />
  </div>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { DateTime } from 'luxon';
import type { PropType } from 'vue';
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import { Loadable, ServiceIncidentType } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useShipmentTimeLineItems } from '@/composables/useShipmentTimeLineItems';
import {
  isEditableShipmentStatus,
  isMilestone,
  isShipmentMilestoneAssociatedEventType,
  Shipment,
  ShipmentEvent,
  ShipmentEventSeverity,
  ShipmentEventType,
  ShipmentServiceIncident,
  ShipmentStop,
  TenderRequest,
} from '@/model';

import ShipmentEventModal from '@/components/shipment/ShipmentEventModal.vue';
import ShipmentServiceIncidentModal from '@/components/shipment/ShipmentServiceIncidentModal.vue';
import ShipmentTimeline from '@/components/shipment/ShipmentTimeline.vue';

const isShowingTimeline = defineModel('showingTimeline', {
  type: Boolean,
  default: () => false,
});

const isSaving = defineModel('saving', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  shipmentEvents: {
    type: Array as PropType<ShipmentEvent[]>,
    default: () => [],
  },
  shipmentServiceIncidents: {
    type: Array as PropType<ShipmentServiceIncident[]>,
    default: () => [],
  },
  shipmentStops: {
    type: Array as PropType<ShipmentStop[]>,
    default: () => [],
  },
  tenderRequests: {
    type: Array as PropType<TenderRequest[]>,
    default: () => [],
  },
  serviceIncidentTypes: {
    type: Object as PropType<Loadable<ServiceIncidentType[]>>,
    default: () => ({ loading: false }),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  showEntriesWithoutLocations: {
    type: Boolean,
    default: () => false,
  },
});
const {
  shipment,
  shipmentEvents,
  shipmentServiceIncidents,
  shipmentStops,
  tenderRequests,
  serviceIncidentTypes,
  editable,
  showEntriesWithoutLocations,
} = toRefs(props);
const emit = defineEmits<{
  (
    event: 'shipment-event-or-service-incident-saved',
    newVal: ShipmentEvent | ShipmentServiceIncident,
  ): void;
}>();

const { t } = useI18n();
const shipmentTimeLineItemsUtil = useShipmentTimeLineItems();
const toast = useToast();

const eventModal = ref({
  visible: false,
  showSource: false,
  event: new ShipmentEvent(),
});

const incidentModal = ref({
  visible: false,
  incident: new ShipmentServiceIncident(),
});

const shipmentEventTypeOptions = mapEnumToTranslatedDropdownValues(
  ShipmentEventType,
  'ShipmentEventType',
).filter((type) => !isMilestone(type.value as ShipmentEventType));

const shipmentEventSeverityOptions = mapEnumToTranslatedDropdownValues(
  ShipmentEventSeverity,
  'ShipmentEventSeverity',
);

const canAddMilestoneEvents = computed(() => {
  return isEditableShipmentStatus(shipment.value.status);
});

const filteredShipmentEventTypeOptions = computed(() =>
  shipmentEventTypeOptions.filter(
    (type) =>
      !isShipmentMilestoneAssociatedEventType(
        type.value as ShipmentEventType,
      ) || canAddMilestoneEvents.value,
  ),
);

const milestones = computed(() =>
  shipmentEvents.value.filter((e) => e.isMilestone),
);

const requireBothArrivalAndDepartureOnStops = computed(
  () => shipment.value.requireBothArrivalAndDepartureOnStops,
);

const shipmentTimelineItems = computed(() => {
  return shipmentTimeLineItemsUtil.toShipmentTimeLineItems(
    shipmentStops.value,
    shipmentEvents.value,
    shipmentServiceIncidents.value,
    tenderRequests.value,
  );
});

const showEventModal = (event: ShipmentEvent) => {
  eventModal.value.event = event;
  eventModal.value.visible = true;
};

const showIncidentModal = (incident: ShipmentServiceIncident) => {
  incidentModal.value.incident = incident;
  incidentModal.value.visible = true;
};

const onSaveShipmentEvent = async (shipmentEvent: ShipmentEvent) => {
  if (!shipment.value.id) {
    return;
  }

  try {
    isSaving.value = true;

    if (shipmentEvent.id) {
      const savedEvent = await api.shipmentEvent.update(
        shipment.value.id,
        shipmentEvent,
      );
      const index = shipmentEvents.value.findIndex(
        (x) => x.id === savedEvent.id,
      );
      shipmentEvents.value.splice(index, 1, savedEvent);
    } else {
      await api.shipmentEvent.create(shipment.value.id, shipmentEvent);
    }

    toast.addSuccess({
      detail: t('messages.successUpdatingParam', { param: t('events.events') }),
    });

    emit('shipment-event-or-service-incident-saved', shipmentEvent);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving or updating shipment event', e);
    toast.addError({
      detail: t('errors.updatingParam', { param: t('events.events') }),
      error: e,
    });
  } finally {
    isSaving.value = false;
    isShowingTimeline.value = true;
  }
};

const onAddShipmentEvent = () => {
  let event = new ShipmentEvent({ actualDate: DateTime.now() });
  eventModal.value.showSource = false;
  showEventModal(event);
};

const onEditShipmentEvent = (event: ShipmentEvent) => {
  eventModal.value.showSource = true;
  showEventModal(event);
};

const onDeleteShipmentEvent = async (event: ShipmentEvent) => {
  if (!shipment.value.id) {
    return;
  }

  isSaving.value = true;
  try {
    await api.shipmentEvent.delete(shipment.value.id, event);
    const index = shipmentEvents.value.findIndex((x) => x.id === event.id);
    shipmentEvents.value.splice(index, 1);
  } finally {
    isSaving.value = false;
  }
};

const onDeleteShipmentServiceIncident = async (
  incident: ShipmentServiceIncident,
) => {
  if (!shipment.value.id) {
    return;
  }

  isSaving.value = true;
  try {
    await api.shipmentServiceIncident.delete(shipment.value.id, incident);
    const index = shipmentServiceIncidents.value.findIndex(
      (x) => x.id === incident.id,
    );
    shipmentServiceIncidents.value.splice(index, 1);
  } finally {
    isSaving.value = false;
  }
};

const onEditShipmentServiceIncident = (incident: ShipmentServiceIncident) => {
  showIncidentModal(incident);
};

const onSaveServiceIncident = async (
  serviceIncident: ShipmentServiceIncident,
) => {
  if (!shipment.value.id) {
    return;
  }

  isSaving.value = true;
  try {
    let saved;
    if (serviceIncident.id) {
      saved = await api.shipmentServiceIncident.update(
        shipment.value.id,
        serviceIncident,
      );
      const index = shipmentServiceIncidents.value.findIndex(
        (x) => x.id === serviceIncident.id,
      );
      shipmentServiceIncidents.value.splice(index, 1, saved);
    } else {
      saved = await api.shipmentServiceIncident.create(
        shipment.value.id,
        serviceIncident,
      );
    }

    toast.addSuccess({
      detail: t('messages.successUpdatingParam', {
        param: t('events.incidents'),
      }),
    });

    emit('shipment-event-or-service-incident-saved', serviceIncident);
  } finally {
    isSaving.value = false;
  }
  isShowingTimeline.value = true;
};

const onAddServiceIncident = () => {
  const incident = new ShipmentServiceIncident({
    carrierId: shipment.value.carrierId,
    actualDate: DateTime.now(),
  });
  showIncidentModal(incident);
};

defineExpose({
  onAddServiceIncident,
  onAddShipmentEvent,
  showEventModal,
  showIncidentModal,
  toggleTimeline: (newVal?: boolean) => {
    isShowingTimeline.value = newVal ?? !isShowingTimeline.value;
  },
});
</script>
